import { useEffect, useState } from "react";
import { History, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { errorMessage, get } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const REASONS = { exited: "Exited", expired: "Timed out", signed_out: "Signed out", replaced: "Opened another company", signed_in_again: "Signed in again" };
const when = (iso) => new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

const STEP = 10;

export const SupportLog = () => {
  const [data, setData] = useState(null);
  const [more, setMore] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async (skip) => {
    setBusy(true);
    try {
      const page = await get("/platform/support-views", { skip, limit: STEP + 1 });
      setMore(page.length > STEP);
      setData((cur) => [...(skip ? cur || [] : []), ...page.slice(0, STEP)]);
    } catch (e) {
      toast.error(errorMessage(e));
      setData((cur) => cur || []);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load(0);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section className="space-y-3" data-testid="support-log">
      <h2 className="flex items-center gap-2 font-display text-base font-bold text-slate-900 md:text-lg"><History className="h-4 w-4" />Support visits</h2>
      <p className="text-sm text-slate-500">Each time you viewed a company as its manager. Only you can see this.</p>
      {data && !data.length && <p className="text-sm text-slate-500" data-testid="support-log-empty">No support visits yet.</p>}
      {data?.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>Company</TableHead><TableHead>Started</TableHead><TableHead>Ended</TableHead><TableHead>How it ended</TableHead></TableRow></TableHeader>
            <TableBody>
              {data.map((s) => (
                <TableRow key={s.id} data-testid={`support-log-row-${s.id}`}>
                  <TableCell className="font-semibold">{s.company_name}</TableCell>
                  <TableCell className="font-data text-sm">{when(s.started_at)}</TableCell>
                  <TableCell className="font-data text-sm">{s.ended_at ? when(s.ended_at) : "In progress"}</TableCell>
                  <TableCell className="text-sm">{REASONS[s.ended_reason] || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {more && (
        <Button variant="outline" onClick={() => load(data.length)} disabled={busy} data-testid="support-log-load-more">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Load more
        </Button>
      )}
    </section>
  );
};
